
/**
 * SHAPE SIZE SELECTOR JAVASCRIPT
 * Shared functionality for all mattress size selectors and grid interaction
 */

document.addEventListener("DOMContentLoaded", function() {
  'use strict'; // Enable strict mode for better error catching
  
  console.log("Shape Size Selector: Shared JS Initializing");
  
  // Create namespace for shape selectors
  window.shapeSelectors = window.shapeSelectors || {};
  window.shapeSelectors.isInitialized = true;
  
  // Add modal transition styles
  addModalStyles();
  
  // Add event listener for mattressShapeSelected at document level
  document.addEventListener("mattressShapeSelected", function(event) {
    setTimeout(function() {
      scrollToMattressGrid();
      updateMattressGridDisplay();
    }, 300);
  });
  
  // Initialize all sections with the mattress-shape-section class
  const allShapeSections = document.querySelectorAll(".mattress-shape-section");
  
  if (allShapeSections.length === 0) {
    console.warn("No shape sections found on the page");
    return;
  }
  
  console.log(`Found ${allShapeSections.length} shape sections, initializing...`);
  
  allShapeSections.forEach(function(shapeSection) {
    const sectionId = shapeSection.getAttribute("data-section-id");
    
    if (!sectionId) {
      console.error("Section ID not found for a shape section");
      return;
    }
    
    initializeSection(shapeSection, sectionId);
  });
  
  // Initialize stored shape display from localStorage
  loadStoredShape();
  
//=============================================================================
// SECTION 1: INITIALIZATION AND UTILITIES
//=============================================================================
  
// Function to initialize a specific section
function initializeSection(shapeSection, sectionId) {
  console.log("Initializing section:", sectionId);
  
  // Core functionality for this specific section
  initToggleFunctionality(shapeSection);
  initSideToggle(shapeSection);
  initModalFunctionality(shapeSection, sectionId);
  initDescriptionLinks(shapeSection, sectionId);
  initShapeSelection(shapeSection, sectionId);
  loadPreviousSelection(shapeSection);
  initConverter(shapeSection, sectionId);
  initCrossComponentEvents(shapeSection);
  
  // Layout and margin fixes
  fixMargins(shapeSection);
  
  console.log("✅ Section fully initialized:", sectionId);
}

// Utility function for safer DOM element selection
function getElement(selector, parent = document) {
  const element = parent.querySelector(selector);
  if (!element) {
    console.warn(`Element not found: ${selector}`);
  }
  return element;
}

// Utility function for safer event binding
function bindEvent(element, eventType, callback) {
  if (!element) {
    console.warn("Cannot bind event - element is null");
    return;
  }
  element.addEventListener(eventType, callback);
}

// Utility function to safely parse JSON
function safeJSONParse(jsonString, fallback = null) {
  try {
    return JSON.parse(jsonString);
  } catch (e) {
    console.error("Error parsing JSON:", e);
    return fallback;
  }
}

// Utility function to debounce function calls (for resize events)
function debounce(func, wait = 300) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// Add styles for modal transitions and section highlighting
function addModalStyles() {
  try {
    // Check if styles already exist
    if (document.getElementById("mattress-connector-styles")) {
      return;
    }
    
    const styleElement = document.createElement("style");
    styleElement.id = "mattress-connector-styles";
    styleElement.textContent = `
      /* Modal transition styles */
      .mattress-shapes-modal {
        transition: opacity 0.5s ease-out;
        opacity: 1;
      }
      .mattress-shapes-modal.fading {
        opacity: 0;
      }
      .mattress-modal-overlay {
        transition: opacity 0.5s ease-out;
        opacity: 1;
      }
      .mattress-modal-overlay.fading {
        opacity: 0;
      }
      .mattress-modal-container {
        transition: transform 0.5s ease-out;
        transform: translateY(0);
      }
      .mattress-shapes-modal.fading .mattress-modal-container {
        transform: translateY(10px);
      }
      
      /* Section highlight styles */
      .mattress-section-highlight {
        border: 1px solid #e5e5e5 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
        transition: border 0.3s ease, box-shadow 0.3s ease !important;
      }
    `;
    document.head.appendChild(styleElement);
    console.log("Shape Size Selector: Added modal transition styles");
  } catch (error) {
    console.error("Error adding modal styles:", error);
  }
}

  /**
 * TOGGLE FUNCTIONALITY WITH AGGRESSIVE OVERRIDES
 * 
 * Note: This implementation uses forceful techniques (important flags, 
 * direct property assignments, and event blocking) to ensure reliable 
 * toggle behavior. This is necessary due to possible conflicts with:
 * 
 * 1. Theme CSS specificity issues
 * 2. Multiple event initializations
 * 3. Transition timing conflicts
 * 
 * A more elegant solution would require comprehensive refactoring of
 * the theme's CSS and event architecture.
 */
  
//=============================================================================
// SECTION 2: EXPAND/COLLAPSE FUNCTIONALITY
//=============================================================================

function initToggleFunctionality(shapeSection) {
  // Find the key elements
  const header = getElement(".mattress-shape-header", shapeSection);
  const content = getElement(".mattress-shape-content", shapeSection);
  const toggleBtn = getElement(".mattress-shape-toggle", shapeSection);
  
  if (!header || !content || !toggleBtn) {
    console.error("Critical toggle elements not found");
    return;
  }
  
  // Prevent multiple initialization
  if (toggleBtn.hasAttribute('data-toggle-initialized')) {
    console.log("Toggle already initialized, skipping");
    return;
  }
  
  console.log("Toggle elements found, initializing toggle...");
  
  // Remove any inline styles that might conflict with our transitions
  content.removeAttribute('style');
  
  // Get transition duration from CSS (or use default)
  const transitionDuration = getTransitionDuration(content) || 300;
  // Add a small buffer to ensure transition completes
  const hideDelay = transitionDuration + 50;
  
  // Check initial state from aria-expanded attribute
  const isInitiallyExpanded = toggleBtn.getAttribute("aria-expanded") === "true";
  console.log("Initial toggle state:", isInitiallyExpanded ? "expanded" : "collapsed");
  
  // Set initial state using classes only
  if (isInitiallyExpanded) {
    shapeSection.classList.add("active");
    header.classList.remove("collapsed");
  } else {
    shapeSection.classList.remove("active");
    header.classList.add("collapsed");
    // Only set display none directly (can't be transitioned)
    content.style.display = "none";
  }
  
  // Replace toggle handler to handle transitions properly
  toggleBtn.onclick = function(e) {
    e.stopPropagation();
    
    const isExpanded = this.getAttribute("aria-expanded") === "true";
    console.log("Toggle button clicked, current state:", isExpanded ? "expanded" : "collapsed");
    
    if (isExpanded) {
      // COLLAPSE - Handle transitions properly
      this.setAttribute("aria-expanded", "false");
      header.classList.add("collapsed");
      shapeSection.classList.remove("active");
      
      // Add a class to control the transition state
      content.classList.add('is-closing');
      
      // Set the transitioning properties with specific height instead of 0
      content.style.opacity = "0";
      content.style.maxHeight = "0px";
      content.style.paddingTop = "0px";
      content.style.paddingBottom = "0px";
      content.style.marginTop = "0px";
      content.style.marginBottom = "0px";
      
      // Clear any previous timeout
      if (content.hideTimeout) clearTimeout(content.hideTimeout);
      
      // Then after transition, set display:none
      content.hideTimeout = setTimeout(() => {
        content.style.display = "none";
        content.classList.remove('is-closing');
      }, hideDelay);
      
    } else {
      // EXPAND - Handle transitions properly
      this.setAttribute("aria-expanded", "true");
      header.classList.remove("collapsed");
      shapeSection.classList.add("active");
      
      // Clear any timeout that might be pending
      if (content.hideTimeout) {
        clearTimeout(content.hideTimeout);
        content.hideTimeout = null;
      }
      
      // Remove the closing class if it exists
      content.classList.remove('is-closing');
      
      // First set display:block
      content.style.display = "block";
      
      // Force reflow to ensure display takes effect
      void content.offsetHeight;
      
      // Clear any transitioning properties
      content.style.paddingTop = "";
      content.style.paddingBottom = "";
      content.style.marginTop = "";
      content.style.marginBottom = "";
      
      // Then set the transitioning properties
      content.style.opacity = "1";
      content.style.maxHeight = "none";
    }
  };
  
  // Replace header handler too
  header.onclick = function(e) {
    if (!e.target.closest(".mattress-shape-toggle")) {
      const toggle = this.querySelector(".mattress-shape-toggle");
      if (toggle) toggle.click();
    }
  };
  
  // Mark as initialized
  toggleBtn.setAttribute('data-toggle-initialized', 'true');
  
  console.log("Toggle functionality initialized with smooth transitions");
}

// Utility function to get transition duration from computed styles
function getTransitionDuration(element) {
  const style = window.getComputedStyle(element);
  const duration = style.transitionDuration;
  
  if (duration && duration !== '') {
    // Convert from "0.3s" to milliseconds
    if (duration.indexOf('ms') > -1) {
      return parseFloat(duration);
    } else {
      return parseFloat(duration) * 1000;
    }
  }
  
  return 0;
}

//=============================================================================
// SECTION 3: SIDE TOGGLE FUNCTIONALITY (NEARSIDE/OFFSIDE) - MAIN VIEW ONLY
//=============================================================================
  
function initSideToggle(shapeSection) {
  const toggleButtons = shapeSection.querySelectorAll(".side-toggle-btn");
  if (toggleButtons.length === 0) {
    console.warn("No side toggle buttons found");
    return;
  }
  
  console.log("Found side toggle buttons:", toggleButtons.length);
  
  // Store the current side selection at the section level
  shapeSection.setAttribute("data-selected-side", "nearside");
  
  toggleButtons.forEach(btn => {
    bindEvent(btn, "click", function() {
      // Remove active class from all buttons
      toggleButtons.forEach(b => b.classList.remove("active"));
      
      // Add active class to clicked button
      this.classList.add("active");
      
      // Get side value (nearside or offside)
      const side = this.getAttribute("data-side");
      
      console.log(`Side toggle clicked: ${side}`);
      
      // Store the selected side at the section level
      shapeSection.setAttribute("data-selected-side", side);
      
      // Toggle visibility of images
      const nearsideImages = shapeSection.querySelectorAll(".mattress-shape-image.nearside");
      const offsideImages = shapeSection.querySelectorAll(".mattress-shape-image.offside");
      
      nearsideImages.forEach(img => img.style.display = side === "nearside" ? "block" : "none");
      offsideImages.forEach(img => img.style.display = side === "offside" ? "block" : "none");
      
      // Update shape names and buttons based on selected side
      const shapeItems = shapeSection.querySelectorAll(".mattress-shape-item");
      
      shapeItems.forEach(item => {
        const nameElement = item.querySelector(".mattress-shape-name");
        const selectBtn = item.querySelector(".mattress-shape-select-btn");
        
        if (nameElement && selectBtn) {
          if (side === "nearside") {
            // Use nearside data
            const nearsideName = item.getAttribute("data-shape-name");
            const nearsideId = item.getAttribute("data-shape-id");
            
            nameElement.textContent = nearsideName;
            selectBtn.setAttribute("data-shape-id", nearsideId);
            selectBtn.setAttribute("data-shape-name", nearsideName);
          } else {
            // Use offside data
            const offsideName = item.getAttribute("data-offside-shape-name");
            const offsideId = item.getAttribute("data-offside-shape-id");
            
            nameElement.textContent = offsideName;
            selectBtn.setAttribute("data-shape-id", offsideId);
            selectBtn.setAttribute("data-shape-name", offsideName);
          }
        }
      });
      
      // If we already have a stored selection, update it with the new side
      updateSelectionSideIfNeeded(side);
    });
  });
}

// Helper function to update the stored selection if it exists
function updateSelectionSideIfNeeded(side) {
  try {
    const storedSelection = localStorage.getItem("mattressSelection");
    if (!storedSelection) return;
    
    const selection = JSON.parse(storedSelection);
    if (!selection) return;
    
    // Update the side
    selection.side = side;
    
    // Find the matching shape item for the current selection
    const shapeItems = document.querySelectorAll(`.mattress-shape-item[data-shape-id="${selection.shapeId}"], .mattress-shape-item[data-offside-shape-id="${selection.shapeId}"]`);
    
    if (shapeItems.length > 0) {
      const shapeItem = shapeItems[0];
      
      // Update shape ID and name based on selected side
      if (side === "nearside") {
        selection.shapeId = shapeItem.getAttribute("data-shape-id");
        selection.shapeName = shapeItem.getAttribute("data-shape-name");
      } else {
        selection.shapeId = shapeItem.getAttribute("data-offside-shape-id");
        selection.shapeName = shapeItem.getAttribute("data-offside-shape-name");
      }
      
      // Update image if available
      const sideImage = shapeItem.querySelector(`.mattress-shape-image.${side}`);
      if (sideImage) {
        selection.shapeImage = sideImage.src;
      }
      
      // Save the updated selection
      localStorage.setItem("mattressSelection", JSON.stringify(selection));
      
      // Update URL builder with the new selection
      updateUrlBuilder(selection);
      
      console.log("Updated stored selection with new side:", side, selection);
    }
  } catch (error) {
    console.error("Error updating stored selection:", error);
  }
}
  
  //=============================================================================
  // SECTION 4: MODAL FUNCTIONALITY
  //=============================================================================
  
  function initModalFunctionality(shapeSection, sectionId) {
    // Get both "View All" buttons and the modal
    const viewAllBtn = document.getElementById(`viewAllShapes${sectionId}`);
    const viewAllBottomBtn = document.getElementById(`viewAllShapesBottom${sectionId}`);
    const modal = document.getElementById(`shapesModal${sectionId}`);
    
    if (!modal) {
      console.error("Modal not found with ID:", `shapesModal${sectionId}`);
      return;
    }
    
    console.log("Modal found, initializing");
    
    const modalCloseBtn = getElement(".mattress-modal-close", modal);
    const modalOverlay = getElement(".mattress-modal-overlay", modal);
    
    // Function to open the modal
    function openModal() {
      console.log("Opening modal");
      modal.style.display = "block";
      
      // Explicitly set the overlay to display
      const overlay = modal.querySelector('.mattress-modal-overlay');
      if (overlay) {
        overlay.style.display = "block";
      }
      
      document.body.style.overflow = "hidden"; // Prevent background scrolling
      
      // Set entry point in session storage
      sessionStorage.setItem('mattressModalEntryPoint', 'viewAll');
      
      // Load shapes if needed
      const shapesLoadedState = modal.getAttribute('data-shapes-loaded') === 'true';
      if (!shapesLoadedState) {
        loadModalShapesFromJson(modal, sectionId, shapeSection);
        modal.setAttribute('data-shapes-loaded', 'true');
        
        // Enhance selection buttons consistently after loading
        setTimeout(() => {
          enhanceModalShapeButtons(modal, sectionId);
        }, 1000);
      }
    }
    
    // Function to close the modal with animation
    function closeModal() {
      console.log("Closing modal");
      
      // Start fade animation
      modal.classList.add("fading");
      
      // Fade overlay
      const overlay = modal.querySelector('.mattress-modal-overlay');
      if (overlay) {
        overlay.classList.add("fading");
      }
      
      // Remove modal after animation
      setTimeout(function() {
        modal.style.display = "none";
        modal.classList.remove("fading");
        document.body.style.overflow = ""; // Re-enable scrolling
        
        // Reset overlay
        if (overlay) {
          overlay.classList.remove("fading");
        }
        
        // Return focus based on entry point
        if (sessionStorage.getItem('mattressModalEntryPoint') === 'viewAll') {
          if (viewAllBtn) viewAllBtn.focus(); // Return focus for accessibility
        }
        
        // Reset entry point
        sessionStorage.removeItem('mattressModalEntryPoint');
      }, 500);
    }
    
    // Open the modal when top "View All" is clicked
    if (viewAllBtn) {
      bindEvent(viewAllBtn, "click", function(e) {
        e.preventDefault();
        console.log("View All button clicked");
        openModal();
      });
    } else {
      console.warn("Top View All button not found with ID:", `viewAllShapes${sectionId}`);
    }
    
    // Open the modal when bottom "View All" is clicked
    if (viewAllBottomBtn) {
      bindEvent(viewAllBottomBtn, "click", function(e) {
        e.preventDefault();
        console.log("View All Bottom button clicked");
        openModal();
      });
    } else {
      console.warn("Bottom View All button not found with ID:", `viewAllShapesBottom${sectionId}`);
    }
    
    // Close button click
    if (modalCloseBtn) {
      bindEvent(modalCloseBtn, "click", closeModal);
    }
    
    // Click outside the modal to close
    if (modalOverlay) {
      bindEvent(modalOverlay, "click", closeModal);
    }
    
    // Close modal when ESC key is pressed
    document.addEventListener("keydown", function(e) {
      if (e.key === "Escape" && modal.style.display === "block") {
        closeModal();
      }
    });
  }

//=============================================================================
// SECTION 4.1: DESCRIPTION LINKS
//=============================================================================

function initDescriptionLinks(shapeSection, sectionId) {
  console.log("Initializing description links");
  
  // Find the viewAllShapes button in the description
  const viewAllBtn = document.getElementById(`viewAllShapes${sectionId}`);
  
  if (viewAllBtn) {
    console.log("Found View All button in description");
    const modal = document.getElementById(`shapesModal${sectionId}`);
    
    if (modal) {
      // Add a specific click handler to directly open the modal
      viewAllBtn.onclick = function(e) {
        e.preventDefault();
        console.log("View All link in description clicked");
        
        // Show the modal directly
        modal.style.display = "block";
        
        // Explicitly set the overlay to display
        const overlay = modal.querySelector('.mattress-modal-overlay');
        if (overlay) {
          overlay.style.display = "block";
        }
        
        document.body.style.overflow = "hidden"; // Prevent background scrolling
      };
    }
  } else {
    console.warn("Description view all button not found");
  }
}

//=============================================================================
// SECTION 4.5: UNIFIED MODAL INITIALIZATION
//=============================================================================

function initModalShapes(modal, sectionId, shapeSection, entryPoint) {
  if (!modal) {
    console.error("Cannot initialize modal shapes - modal is null");
    return;
  }
  
  // Set entry point in session storage
  sessionStorage.setItem('mattressModalEntryPoint', entryPoint || 'viewAll');
  
  // Show the modal
  modal.style.display = "block";
  
  // Explicitly set the overlay to display
  const overlay = modal.querySelector('.mattress-modal-overlay');
  if (overlay) {
    overlay.style.display = "block";
  } else {
    console.warn("Modal overlay not found");
  }
  
  document.body.style.overflow = "hidden"; // Prevent scrolling
  
  // Focus on close button for accessibility
  const modalCloseBtn = getElement(".mattress-modal-close", modal);
  if (modalCloseBtn) {
    modalCloseBtn.focus();
  }
  
  // Load shapes if needed
  const shapesLoadedState = modal.getAttribute('data-shapes-loaded') === 'true';
  if (!shapesLoadedState) {
    console.log("Loading shapes for modal - first time initialization");
    loadModalShapesFromJson(modal, sectionId, shapeSection);
    modal.setAttribute('data-shapes-loaded', 'true');
    
    // Enhance selection buttons consistently after loading
    setTimeout(() => {
      enhanceModalShapeButtons(modal, sectionId);
    }, 1000);
  }
}

// Centralized function to enhance modal shape buttons with consistent behavior
function enhanceModalShapeButtons(modal, sectionId) {
  if (!modal) return;
  
  const modalButtons = modal.querySelectorAll('.mattress-shape-select-btn');
  console.log(`Enhancing ${modalButtons.length} shape buttons in modal`);
  
  modalButtons.forEach(btn => {
    // Create new handler that preserves any existing functionality
    const originalHandler = btn.onclick;
    
    btn.onclick = function(e) {
      // Get shape data from button attributes
      const shapeId = this.getAttribute("data-shape-id");
      const shapeName = this.getAttribute("data-shape-name");
      const size = this.getAttribute("data-size");
      
      console.log(`Modal shape selected: ${shapeName} (${shapeId}), size: ${size}`);
      
      // Get the shape image URL if available
      let shapeImage = "";
      const shapeItem = this.closest(".mattress-shape-item");
      const imgElement = shapeItem ? shapeItem.querySelector(".mattress-shape-image") : null;
      if (imgElement) {
        shapeImage = imgElement.src;
      }
      
      // Save selection to localStorage
      const selection = {
        size: size,
        shapeId: shapeId,
        shapeName: shapeName,
        shapeImage: shapeImage
      };
      
      try {
        // Save full object
        localStorage.setItem("mattressSelection", JSON.stringify(selection));
        
        // Save individual properties for compatibility with other scripts
        localStorage.setItem("selectedShapeId", selection.shapeId);
        localStorage.setItem("selectedShapeName", selection.shapeName);
        localStorage.setItem("currentSizeCategory", selection.size);
        localStorage.setItem("selectedShapeImage", selection.shapeImage);
      } catch (error) {
        console.warn("Could not save to localStorage:", error);
      }
      
      // Dispatch an event for other components to listen for
      const selectionEvent = new CustomEvent("mattressShapeSelected", {
        detail: selection
      });
      
      document.dispatchEvent(selectionEvent);
      
      // Provide visual feedback
      this.textContent = "Selected";
      this.disabled = true;  // Prevent double-clicks
      
      // Update URL builder if it exists
      updateUrlBuilder(selection);
      
      // Close modal
      if (modal) {
        // Start fade animation
        modal.classList.add("fading");
        
        // Fade overlay
        const overlay = modal.querySelector('.mattress-modal-overlay');
        if (overlay) {
          overlay.classList.add("fading");
        }
        
        // Remove modal after animation
        setTimeout(function() {
          modal.style.display = "none";
          modal.classList.remove("fading");
          document.body.style.overflow = ""; // Re-enable scrolling
          
          // Reset overlay
          if (overlay) {
            overlay.classList.remove("fading");
          }
        }, 500);
      }
      
      // Execute original handler if it exists
      if (originalHandler && typeof originalHandler === 'function') {
        originalHandler.call(this, e);
      }
      
      // Reset button state after delay
      setTimeout(() => {
        this.textContent = "Select";
        this.disabled = false;
      }, 1000);
    };
  });
}

//=============================================================================
// SECTION 5: LOAD MODAL SHAPES FROM JSON
//=============================================================================

function loadModalShapesFromJson(modal, sectionId, shapeSection) {
  console.log("Loading shapes from JSON");
  
  if (!modal) {
    console.error("Modal is null - cannot load shapes");
    return;
  }
  
  // Get the size for this section
  const sectionSize = shapeSection.getAttribute("data-size") || "single";
  console.log(`Loading shapes for size: ${sectionSize}`);
  
  // Try different sources for the JSON data with fallbacks
  tryLoadShapesData(modal, sectionId, shapeSection, sectionSize);
}

function tryLoadShapesData(modal, sectionId, shapeSection, sectionSize) {
  // First try: Check if inline data is available
  if (window.mattressShapesData) {
    console.log("Using inline JSON data");
    try {
      processShapesData(window.mattressShapesData, modal, sectionId, shapeSection, sectionSize);
      return;
    } catch (error) {
      console.error("Error processing inline JSON data:", error);
      // Continue to next method if this fails
    }
  }
  
  // Second try: Check if Shopify asset URL is available
  const shopifyAssetUrl = window.jsonAssetUrl;
  if (shopifyAssetUrl) {
    console.log("Using Shopify asset URL:", shopifyAssetUrl);
    fetchJsonFromUrl(shopifyAssetUrl, modal, sectionId, shapeSection, sectionSize);
    return;
  }
  
  // Third try: Try standard paths with fallbacks
  const possiblePaths = [
    '/assets/caravan-mattress-shapes.json',
    '/assets/caravan-mattress-shapes.js',
    '/cdn/shop/assets/caravan-mattress-shapes.json',
    '/files/caravan-mattress-shapes.json'
  ];
  
  // Try each path
  tryNextPath(0);
  
  function tryNextPath(index) {
    if (index >= possiblePaths.length) {
      // All paths failed, show error
      console.error("All JSON paths failed");
      showModalErrorMessage(modal, "Could not load mattress shapes. Please refresh and try again.");
      return;
    }
    
    const path = possiblePaths[index];
    console.log(`Trying JSON path ${index + 1}/${possiblePaths.length}: ${path}`);
    
    fetchJsonFromUrl(
      path, 
      modal, 
      sectionId, 
      shapeSection, 
      sectionSize,
      // On failure callback - try next path
      () => tryNextPath(index + 1)
    );
  }
}

function fetchJsonFromUrl(url, modal, sectionId, shapeSection, sectionSize, onFailure) {
  // Create an abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
    console.warn(`Fetch timeout for ${url}`);
    if (onFailure) onFailure();
  }, 8000); // 8 second timeout
  
  fetch(url, { 
    signal: controller.signal,
    headers: {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  })
  .then(response => {
    clearTimeout(timeoutId);
    console.log(`Response from ${url}:`, response.status);
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  })
  .then(data => {
    if (!data || !data.shapeCategories) {
      console.error("Invalid JSON data format - missing shapeCategories");
      throw new Error("Invalid JSON data structure");
    }
    
    console.log("JSON data loaded successfully from:", url);
    console.log("Categories found:", Object.keys(data.shapeCategories));
    
    // Save to window for future use
    window.mattressShapesData = data;
    
    // Process the data
    processShapesData(data, modal, sectionId, shapeSection, sectionSize);
  })
  .catch(error => {
    console.error(`Error loading shapes JSON from ${url}:`, error);
    if (onFailure) onFailure();
  });
}

function processShapesData(data, modal, sectionId, shapeSection, sectionSize) {
  // Get the modal shape grid
  const modalGrid = getElement(".mattress-modal-shape-grid", modal);
  if (!modalGrid) {
    console.error("Modal grid not found");
    showModalErrorMessage(modal, "Modal content area not found. Please refresh and try again.");
    return;
  }
  
  // Clear existing shapes
  modalGrid.innerHTML = "";
  
  // Use provided size or get from section
  sectionSize = sectionSize || shapeSection.getAttribute("data-size") || "single";
  
  // Create an array to hold all shapes from all categories
  const allShapes = [];
  
  // Collect all shapes from all categories
  if (data && data.shapeCategories) {
    for (const categoryKey in data.shapeCategories) {
      const category = data.shapeCategories[categoryKey];
      
      // Skip empty categories
      if (!category || !category.shapes) continue;
      
      // Loop through shapes in this category and add to array
      for (const shapeKey in category.shapes) {
        const shape = category.shapes[shapeKey];
        // Add category info to shape for grouping
        if (shape) {
          allShapes.push({
            ...shape,
            categoryLabel: category.label || categoryKey
          });
        }
      }
    }
  }
  
  console.log(`Found ${allShapes.length} shapes in JSON`);
  
  // If we have shapes, add them to the modal
  if (allShapes.length > 0) {
    // Group shapes by category
    const groupedShapes = {};
    allShapes.forEach(shape => {
      if (!groupedShapes[shape.categoryLabel]) {
        groupedShapes[shape.categoryLabel] = [];
      }
      groupedShapes[shape.categoryLabel].push(shape);
    });
    
    // Create a document fragment for better performance
    const fragment = document.createDocumentFragment();
    
    // Add shapes to modal, organized by category
    Object.keys(groupedShapes).forEach(categoryLabel => {
      // Create category title
      const categoryTitle = document.createElement("div");
      categoryTitle.className = "mattress-shape-category-title";
      categoryTitle.textContent = categoryLabel;
      fragment.appendChild(categoryTitle);
      
      // Add shapes for this category
      groupedShapes[categoryLabel].forEach(shape => {
        const shapeItem = document.createElement("div");
        shapeItem.className = "mattress-shape-item";
        shapeItem.setAttribute("data-shape-id", shape.handle);
        shapeItem.setAttribute("data-shape-name", shape.name);
        
        // Ensure valid image URL with fallback
        const placeholderImg = "/assets/placeholder-shape.png";
        const imageUrl = shape.imageUrl || placeholderImg;
        
        // Create image with error handling
        const imageHtml = `
          <div class="mattress-shape-image-container">
            <img class="mattress-shape-image" 
                 src="${imageUrl}" 
                 alt="${shape.name}" 
                 loading="lazy"
                 onerror="this.onerror=null; this.src='${placeholderImg}'; console.warn('Failed to load image: ${imageUrl}');">
          </div>
        `;
        
        // Build complete shape item HTML
        shapeItem.innerHTML = `
          ${imageHtml}
          <h3 class="mattress-shape-name">
            ${shape.name}
          </h3>
          <button class="mattress-shape-select-btn" 
                  data-shape-id="${shape.handle}" 
                  data-shape-name="${shape.name}" 
                  data-size="${sectionSize}">
            Select
          </button>
        `;
        
        fragment.appendChild(shapeItem);
      });
    });
    
    // Add all elements to the DOM at once (better performance)
    modalGrid.appendChild(fragment);
    
    console.log("Shapes added to modal");
    
    // Add responsive styling for the modal grid
    addModalGridStyles();
    
    // Re-initialize the selection events for the newly created buttons
    initModalSelectButtons(modal, sectionId);
  } else {
    showModalErrorMessage(modal, "No mattress shapes found. Please try again later or contact support.");
  }
}

function showModalErrorMessage(modal, message = "Sorry, we couldn't load the mattress shapes. Please refresh the page or try again later.") {
  if (!modal) return;
  
  const modalGrid = getElement(".mattress-modal-shape-grid", modal);
  if (modalGrid) {
    modalGrid.innerHTML = `
      <div style="text-align: center; width: 100%; padding: 30px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff7700" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p style="margin-top: 15px; color: #444; font-size: 16px;">${message}</p>
        <button id="retryLoadShapes" style="margin-top: 15px; padding: 8px 20px; background-color: #ffa236; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Try Again
        </button>
      </div>
    `;
    
    // Add event listener to retry button
    const retryBtn = modal.querySelector("#retryLoadShapes");
    if (retryBtn) {
      bindEvent(retryBtn, "click", function() {
        const sectionId = modal.getAttribute("data-section-id");
        const sectionSelector = `.mattress-shape-section[data-section-id="${sectionId}"]`;
        const shapeSection = document.querySelector(sectionSelector);
        
        if (shapeSection) {
          loadModalShapesFromJson(modal, sectionId, shapeSection);
        }
      });
    }
  }
}

function addModalGridStyles() {
  // Check if styles already exist
  if (document.getElementById('mattress-modal-grid-styles')) return;
  
  const styleEl = document.createElement("style");
  styleEl.id = 'mattress-modal-grid-styles';
  styleEl.textContent = `
    .mattress-shape-category-title {
      width: 100%;
      padding: 15px;
      margin: 20px 0 10px 0;
      font-size: 22px;
      font-weight: 700;
      color: #403333;
      border-bottom: 1px solid #ddd;
      grid-column: 1 / -1;
      background-color: #f5f5f5;
      border-radius: 5px;
    }
    .mattress-shape-category-title:first-child {
      margin-top: 0;
    }
    .mattress-modal-shape-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
    }
    @media (max-width: 991px) {
      .mattress-modal-shape-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }
    @media (max-width: 767px) {
      .mattress-modal-shape-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @media (max-width: 479px) {
      .mattress-modal-shape-grid {
        grid-template-columns: 1fr;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

//=============================================================================
// SECTION 6: SHAPE SELECTION FUNCTIONALITY
//=============================================================================

function initShapeSelection(shapeSection, sectionId) {
  // Get all select buttons in the main view
  const selectButtons = shapeSection.querySelectorAll(".mattress-shape-select-btn");
  
  if (selectButtons.length > 0) {
    console.log(`Found ${selectButtons.length} shape select buttons`);
    
    // Shared handler for selection buttons
    function handleShapeSelection(e) {
      const btn = e.currentTarget;
      
      // Get shape data from button attributes (already updated by side toggle if needed)
      const shapeId = btn.getAttribute("data-shape-id");
      const shapeName = btn.getAttribute("data-shape-name");
      const size = btn.getAttribute("data-size");
      
      // Get the current side from the section
      const side = shapeSection.getAttribute('data-selected-side') || 'nearside';
      
      console.log(`Shape selected: ${shapeName} (${shapeId}), size: ${size}, side: ${side}`);
      
      // Remove selected state from all shape items
      document.querySelectorAll(".mattress-shape-item").forEach(item => {
        item.classList.remove("selected");
      });
      
      // Add selected state to this shape item
      const shapeItem = btn.closest(".mattress-shape-item");
      if (shapeItem) {
        shapeItem.classList.add("selected");
      }
      
      // Get the shape image URL for the selected side
      let currentImage = "";
      if (shapeItem) {
        const sideImage = shapeItem.querySelector(`.mattress-shape-image.${side}`);
        if (sideImage) {
          currentImage = sideImage.src;
        } else {
          // Fallback to any available image
          const anyImage = shapeItem.querySelector(".mattress-shape-image");
          if (anyImage) {
            currentImage = anyImage.src;
          }
        }
      }
      
      // Save selection to localStorage with side information
      const selection = {
        size: size,
        shapeId: shapeId,
        shapeName: shapeName, 
        shapeImage: currentImage,
        side: side
      };
      
      try {
        // Save full object
        localStorage.setItem("mattressSelection", JSON.stringify(selection));
        
        // Save individual properties for compatibility with other scripts
        localStorage.setItem("selectedShapeId", selection.shapeId);
        localStorage.setItem("selectedShapeName", selection.shapeName);
        localStorage.setItem("currentSizeCategory", selection.size);
        localStorage.setItem("selectedShapeImage", selection.shapeImage);
        localStorage.setItem("selectedSide", selection.side);
      } catch (error) {
        console.warn("Could not save to localStorage:", error);
      }
      
      // Dispatch an event for other components to listen for
      const selectionEvent = new CustomEvent("mattressShapeSelected", {
        detail: selection
      });
      
      document.dispatchEvent(selectionEvent);
      
      // Provide visual feedback
      const originalText = btn.textContent;
      btn.textContent = "Selected";
      btn.disabled = true;  // Prevent double-clicks
      
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
      
      // Update URL builder with side information
      updateUrlBuilder(selection);
      
      // Close modal if this is a modal button
      const modal = document.getElementById(`shapesModal${sectionId}`);
      if (modal && modal.contains(btn)) {
        modal.style.display = "none";
        document.body.style.overflow = "";
      }
    }
    
    // Add event listeners to all buttons
    selectButtons.forEach(btn => {
      bindEvent(btn, "click", handleShapeSelection);
    });
  } else {
    console.warn("No shape select buttons found");
  }
}

// Updated modal selection handling
function initModalSelectButtons(modal, sectionId) {
  if (!modal) return;
  
  const modalButtons = modal.querySelectorAll('.mattress-shape-select-btn');
  console.log(`Enhancing ${modalButtons.length} shape buttons in modal`);
  
  modalButtons.forEach(btn => {
    // Create new handler that preserves any existing functionality
    const originalHandler = btn.onclick;
    
    btn.onclick = function(e) {
      // Get shape data from button attributes
      const shapeId = this.getAttribute("data-shape-id");
      const shapeName = this.getAttribute("data-shape-name");
      const size = this.getAttribute("data-size");
      
      // Default to nearside for modal buttons since we don't have a toggle
      const side = "nearside";
      
      console.log(`Modal shape selected: ${shapeName} (${shapeId}), size: ${size}, side: ${side}`);
      
      // Get the shape image URL if available
      let shapeImage = "";
      const shapeItem = this.closest(".mattress-shape-item");
      const imgElement = shapeItem ? shapeItem.querySelector(".mattress-shape-image") : null;
      if (imgElement) {
        shapeImage = imgElement.src;
      }
      
      // Save selection to localStorage with side information
      const selection = {
        size: size,
        shapeId: shapeId,
        shapeName: shapeName,
        shapeImage: shapeImage,
        side: side
      };
      
      try {
        // Save full object
        localStorage.setItem("mattressSelection", JSON.stringify(selection));
        
        // Save individual properties for compatibility with other scripts
        localStorage.setItem("selectedShapeId", selection.shapeId);
        localStorage.setItem("selectedShapeName", selection.shapeName);
        localStorage.setItem("currentSizeCategory", selection.size);
        localStorage.setItem("selectedShapeImage", selection.shapeImage);
        localStorage.setItem("selectedSide", selection.side);
      } catch (error) {
        console.warn("Could not save to localStorage:", error);
      }
      
      // Dispatch an event for other components to listen for
      const selectionEvent = new CustomEvent("mattressShapeSelected", {
        detail: selection
      });
      
      document.dispatchEvent(selectionEvent);
      
      // Provide visual feedback
      this.textContent = "Selected";
      this.disabled = true;  // Prevent double-clicks
      
      // Update URL builder with side information
      updateUrlBuilder(selection);
      
      // Close modal with animation
      if (modal) {
        // Start fade animation
        modal.classList.add("fading");
        
        // Fade overlay
        const overlay = modal.querySelector('.mattress-modal-overlay');
        if (overlay) {
          overlay.classList.add("fading");
        }
        
        // Remove modal after animation
        setTimeout(function() {
          modal.style.display = "none";
          modal.classList.remove("fading");
          document.body.style.overflow = ""; // Re-enable scrolling
          
          // Reset overlay
          if (overlay) {
            overlay.classList.remove("fading");
          }
        }, 500);
      }
      
      // Execute original handler if it exists
      if (originalHandler && typeof originalHandler === 'function') {
        originalHandler.call(this, e);
      }
      
      // Reset button state after delay
      setTimeout(() => {
        this.textContent = "Select";
        this.disabled = false;
      }, 1000);
    };
  });
}

//=============================================================================
// SECTION 7: URL BUILDER INTEGRATION
//=============================================================================

// Function to update the URL builder with the selected shape
function updateUrlBuilder(selection) {
  // Find URL builder if it exists on the page
  const urlBuilder = document.querySelector(".mattress-url-builder");
  if (!urlBuilder) {
    console.log("URL builder not found - skipping update");
    return;
  }
  
  console.log("Updating URL builder with selection", selection);
  
  // Update shape field in URL builder
  const shapeField = urlBuilder.querySelector('[data-field="shape"]');
  if (shapeField) {
    shapeField.textContent = selection.shapeName || "Not selected";
    shapeField.setAttribute("data-value", selection.shapeId || "");
  }
  
  // Update size field in URL builder
  const sizeField = urlBuilder.querySelector('[data-field="size"]');
  if (sizeField) {
    let sizeText = "Single";
    switch(selection.size) {
      case "double": 
        sizeText = "Double"; 
        break;
      case "small-double": 
        sizeText = "Small Double"; 
        break;
      case "king": 
        sizeText = "King"; 
        break;
      case "super-king": 
        sizeText = "Super King"; 
        break;
    }
    
    sizeField.textContent = sizeText;
    sizeField.setAttribute("data-value", selection.size || "");
  }
  
  // Update side information in URL builder (add if it doesn't exist)
  let sideField = urlBuilder.querySelector('[data-field="side"]');
  
  // If side field doesn't exist, create it
  if (!sideField && selection.side) {
    // Find a good place to insert the new field
    const fieldList = urlBuilder.querySelector('.mattress-fields-list');
    if (fieldList) {
      // Create new side field element
      const sideFieldElement = document.createElement('div');
      sideFieldElement.className = 'mattress-field-item';
      sideFieldElement.innerHTML = `
        <span class="mattress-field-label">Cut Side:</span>
        <span class="mattress-field-value" data-field="side">
          ${selection.side === "nearside" ? "Left Cut" : "Right Cut"}
        </span>
      `;
      
      // Insert after shape field
      const shapeFieldItem = shapeField ? shapeField.closest('.mattress-field-item') : null;
      if (shapeFieldItem && shapeFieldItem.nextSibling) {
        fieldList.insertBefore(sideFieldElement, shapeFieldItem.nextSibling);
      } else {
        fieldList.appendChild(sideFieldElement);
      }
      
      sideField = sideFieldElement.querySelector('[data-field="side"]');
    }
  }
  
  // Update side field if it exists now
  if (sideField && selection.side) {
    const sideText = selection.side === "nearside" ? "Left Cut" : "Right Cut";
    sideField.textContent = sideText;
    sideField.setAttribute("data-value", selection.side);
  }
  
  // Update the final URL
  updateFinalUrl();
}

// Function to update the final product URL
function updateFinalUrl() {
  const urlBuilder = document.querySelector(".mattress-url-builder");
  if (!urlBuilder) return;
  
  // Get all selected values
  const size = urlBuilder.querySelector('[data-field="size"]')?.getAttribute("data-value") || "";
  const shape = urlBuilder.querySelector('[data-field="shape"]')?.getAttribute("data-value") || "";
  const side = urlBuilder.querySelector('[data-field="side"]')?.getAttribute("data-value") || "";
  const material = urlBuilder.querySelector('[data-field="material"]')?.getAttribute("data-value") || "";
  const thickness = urlBuilder.querySelector('[data-field="thickness"]')?.getAttribute("data-value") || "";
  
  // Construct the URL with proper encoding
  let productUrl = "/products/custom-mattress?";
  
  // Add query parameters with proper encoding
  const params = [];
  if (size) params.push(`size=${encodeURIComponent(size)}`);
  if (shape) params.push(`shape=${encodeURIComponent(shape)}`);
  if (side) params.push(`side=${encodeURIComponent(side)}`);
  if (material) params.push(`material=${encodeURIComponent(material)}`);
  if (thickness) params.push(`thickness=${encodeURIComponent(thickness)}`);
  
  productUrl += params.join('&');
  
  console.log("Updated product URL:", productUrl);
  
  // Update the URL display and button
  const urlDisplay = urlBuilder.querySelector(".mattress-url-display");
  if (urlDisplay) {
    urlDisplay.textContent = productUrl;
  }
  
  const continueBtn = urlBuilder.querySelector(".mattress-continue-btn");
  if (continueBtn) {
    continueBtn.setAttribute("href", productUrl);
    
    // Enable/disable button based on whether all required fields are selected
    const allRequiredSelected = size && shape;
    continueBtn.disabled = !allRequiredSelected;
    
    // Use classList.toggle for better performance
    continueBtn.classList.toggle("disabled", !allRequiredSelected);
  }
}

//=============================================================================
// SECTION 8: LOAD PREVIOUSLY SELECTED SHAPE
//=============================================================================

// Load stored shape from localStorage for grid display
function loadStoredShape() {
  try {
    const storedSelection = localStorage.getItem("mattressSelection");
    if (storedSelection) {
      const selection = JSON.parse(storedSelection);
      console.log("Loaded stored shape selection for grid", selection);
      
      // Update grid display with stored selection
      updateMattressGridDisplay();
    }
  } catch (error) {
    console.error("Error loading stored shape:", error);
  }
}

// Load any previously selected shape from localStorage for section display
function loadPreviousSelection(shapeSection) {
  try {
    const savedSelection = localStorage.getItem("mattressSelection");
    if (!savedSelection) {
      console.log("No previous selection found");
      return;
    }
    
    const selection = safeJSONParse(savedSelection);
    if (!selection) return;
    
    console.log("Found previous selection:", selection);
    
    // Only apply if this is the correct size section
    if (selection.size !== shapeSection.getAttribute("data-size")) {
      console.log("Previous selection is for a different size section");
      return;
    }
    
    // Find the matching shape item
    const shapeItem = shapeSection.querySelector(`.mattress-shape-item[data-shape-id="${selection.shapeId}"]`);
    if (!shapeItem) {
      console.warn("Previously selected shape not found in this section");
      return;
    }
    
    // Mark as selected
    shapeItem.classList.add("selected");
    console.log("Applied previous selection");
    
    // Update URL builder
    updateUrlBuilder(selection);
  } catch (error) {
    console.error("Error loading previous selection:", error);
  }
}

//=============================================================================
// SECTION 9: MEASUREMENT CONVERTER MODAL
//=============================================================================

function initConverter(shapeSection, sectionId) {
  console.log("Initializing measurement converter");
  
  // Get modal elements
  const openConverterBtn = getElement("#open-converter-btn", shapeSection);
  const converterModal = document.getElementById("converterModal");
  
  if (!openConverterBtn || !converterModal) {
    console.warn("Converter elements not found");
    return;
  }
  
  console.log("Converter elements found");
  
  const modalCloseBtn = getElement(".converter-modal-close", converterModal);
  const modalOverlay = getElement(".converter-modal-overlay", converterModal);
  
  // Input fields
  const mmInput = getElement("#modal-mm-input", converterModal);
  const cmInput = getElement("#modal-cm-input", converterModal);
  const inchInput = getElement("#modal-inch-input", converterModal);
  const clearBtn = getElement("#modal-converter-clear", converterModal);
  
  if (!mmInput || !cmInput || !inchInput || !clearBtn) {
    console.error("Converter input fields not found");
    return;
  }
  
  // Open modal when the converter button is clicked
  bindEvent(openConverterBtn, "click", function() {
    console.log("Converter button clicked, opening modal");
    converterModal.style.display = "block";
    
    // Explicitly set the overlay to display
    const overlay = converterModal.querySelector('.converter-modal-overlay');
    if (overlay) {
      overlay.style.display = "block";
    }
    
    document.body.style.overflow = "hidden"; // Prevent background scrolling
    mmInput.focus(); // Focus on first input for accessibility
  });
  
  // Function to close the modal
  function closeConverterModal() {
    console.log("Closing converter modal");
    converterModal.style.display = "none";
    
    // Explicitly hide the overlay
    const overlay = converterModal.querySelector('.converter-modal-overlay');
    if (overlay) {
      overlay.style.display = "none";
    }
    
    document.body.style.overflow = ""; // Re-enable scrolling
    openConverterBtn.focus(); // Return focus for accessibility
  }
  
  // Close button click
  if (modalCloseBtn) {
    bindEvent(modalCloseBtn, "click", function(e) {
      e.preventDefault(); // Prevent default action
      e.stopPropagation(); // Stop event propagation
      closeConverterModal();
    });
  }
  
  // Click outside to close
  if (modalOverlay) {
    bindEvent(modalOverlay, "click", closeConverterModal);
  }
  
  // ESC key to close
  bindEvent(document, "keydown", function(e) {
    if (e.key === "Escape" && converterModal.style.display === "block") {
      closeConverterModal();
    }
  });
  
  // Converter functionality
  let activeInput = null;
  let isUpdating = false; // Flag to prevent recursive updates
  
  // Add debouncing to input handlers for better performance
  const handleMMInput = debounce(function() {
    if (isUpdating) return;
    isUpdating = true;
    
    activeInput = "mm";
    const mmValue = parseFloat(this.value);
    
    if (!isNaN(mmValue)) {
      cmInput.value = (mmValue / 10).toFixed(1);
      inchInput.value = (mmValue / 25.4).toFixed(2);
    } else {
      cmInput.value = "";
      inchInput.value = "";
    }
    
    isUpdating = false;
  }, 100);
  
  const handleCMInput = debounce(function() {
    if (isUpdating) return;
    isUpdating = true;
    
    activeInput = "cm";
    const cmValue = parseFloat(this.value);
    
    if (!isNaN(cmValue)) {
      mmInput.value = (cmValue * 10).toFixed(0);
      inchInput.value = (cmValue / 2.54).toFixed(2);
    } else {
      mmInput.value = "";
      inchInput.value = "";
    }
    
    isUpdating = false;
  }, 100);
  
  const handleInchInput = debounce(function() {
    if (isUpdating) return;
    isUpdating = true;
    
    activeInput = "inch";
    const inchValue = parseFloat(this.value);
    
    if (!isNaN(inchValue)) {
      mmInput.value = (inchValue * 25.4).toFixed(0);
      cmInput.value = (inchValue * 2.54).toFixed(1);
    } else {
      mmInput.value = "";
      cmInput.value = "";
    }
    
    isUpdating = false;
  }, 100);
  
  // Bind input handlers
  bindEvent(mmInput, "input", handleMMInput);
  bindEvent(cmInput, "input", handleCMInput);
  bindEvent(inchInput, "input", handleInchInput);
  
  // Clear button functionality
  bindEvent(clearBtn, "click", function() {
    mmInput.value = "";
    cmInput.value = "";
    inchInput.value = "";
    activeInput = null;
    mmInput.focus(); // Refocus on first input
  });
}

//=============================================================================
// SECTION 10: CROSS-COMPONENT COMMUNICATION & GRID CONNECTION
//=============================================================================

function initCrossComponentEvents(shapeSection) {
  // Listen for selections from other shape sections
  bindEvent(document, "mattressShapeSelected", function(event) {
    // If this is from a different size section, we may need to update the URL builder
    if (event.detail.size !== shapeSection.getAttribute("data-size")) {
      console.log("Received selection event from different section, updating URL builder");
      updateUrlBuilder(event.detail);
    }
  });
}

/**
 * Scroll to mattress grid section (Step 2) with permanent grey border
 */
function scrollToMattressGrid() {
  try {
    // Find the Step 2 section using ID
    const mattressSection = document.querySelector("#MattressStep2");
    
    if (!mattressSection) {
      console.warn("Cannot find Step 2 section");
      return;
    }
    
    console.log("Scrolling to Step 2");
    
    // Calculate position with header offset
    const headerOffset = 80;
    const elementRect = mattressSection.getBoundingClientRect();
    const absoluteY = window.pageYOffset + elementRect.top - headerOffset;
    
    // Scroll to position
    window.scrollTo({
      top: absoluteY,
      behavior: "smooth"
    });
    
    // Add permanent highlight effect
    mattressSection.style.border = "1px solid #e5e5e5";
    mattressSection.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
    
    // Optional: You can add a brief "pulse" effect first, then settle on the permanent border
    mattressSection.classList.add("mattress-section-highlight");
    
    setTimeout(function() {
      mattressSection.classList.remove("mattress-section-highlight");
      // The permanent border/shadow styles remain in place
    }, 2000);
  } catch (error) {
    console.error("Error scrolling to mattress grid:", error);
  }
}

/**
 * Update mattress grid with selected shape display
 */
function updateMattressGridDisplay() {
  try {
    // Check if mattressGrid object exists
    if (window.mattressGrid) {
      // Call methods if available
      if (typeof window.mattressGrid.loadStoredSelections === "function") {
        window.mattressGrid.loadStoredSelections();
      }
      
      if (typeof window.mattressGrid.updateSelectedShapeDisplay === "function") {
        window.mattressGrid.updateSelectedShapeDisplay();
      }
    }
  } catch (error) {
    console.error("Error updating mattress grid display:", error);
  }
}

//=============================================================================
// SECTION 11: MOBILE LAYOUT AND MARGIN FIXES
//=============================================================================

function fixMargins(shapeSection) {
  // Enforce minimum margins for vertical spacing
  const topMargin = parseInt(shapeSection.style.marginTop) || 0;
  const bottomMargin = parseInt(shapeSection.style.marginBottom) || 0;
  
  if (topMargin < 30) {
    shapeSection.style.marginTop = "30px !important";
  }
  
  if (bottomMargin < 30) {
    shapeSection.style.marginBottom = "30px !important";
  }
  
  // Add !important flag to margins
  if (!shapeSection.style.marginTop.includes("!important")) {
    shapeSection.style.marginTop = shapeSection.style.marginTop + " !important";
  }
  
  if (!shapeSection.style.marginBottom.includes("!important")) {
    shapeSection.style.marginBottom = shapeSection.style.marginBottom + " !important";
  }
  
  // SPECIFICALLY target S7+ dimensions only
  const isLandscape = window.innerHeight < window.innerWidth;
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // S7+ has width ~1600 and height ~1200 in landscape
  const isSamsungS7Plus = width >= 1500 && width <= 1700 && height >= 1000 && height <= 1300;
  
  if (isLandscape && isSamsungS7Plus) {
    // Fix the section first (S7+ specific)
    shapeSection.style.width = "100%";
    shapeSection.style.maxWidth = "100%";
    shapeSection.style.paddingLeft = "0";
    shapeSection.style.paddingRight = "0";
    shapeSection.style.overflowX = "hidden";
    
    // Now fix the container (S7+ specific)
    const container = shapeSection.querySelector('.mattress-shape-container');
    if (container) {
      // Force explicit margins and width - larger margins for S7+
      container.style.maxWidth = "calc(100% - 100px)";
      container.style.marginLeft = "50px";
      container.style.marginRight = "50px";
      container.style.boxSizing = "border-box";
      
      // Force !important flags
      const styles = [
        'maxWidth', 'marginLeft', 'marginRight', 'boxSizing'
      ];
      
      styles.forEach(prop => {
        if (container.style[prop] && !container.style[prop].includes("!important")) {
          container.style[prop] += " !important";
        }
      });
      
      console.log("Fixed Samsung S7+ specific landscape margins");
    }
  }
  
  console.log("Margins fixed:", shapeSection.style.marginTop, shapeSection.style.marginBottom);
}

function fixTabletLandscapeMargins() {
  // This is a simplified implementation to ensure margins are correct on tablet landscape
  const allShapeSections = document.querySelectorAll('.mattress-shape-section');
  
  // Check if we're in landscape mode on a tablet-sized device
  const isLandscape = window.innerHeight < window.innerWidth;
  const isTabletSize = window.innerWidth >= 768 && window.innerWidth <= 1366;
  
  if (isLandscape && isTabletSize) {
    allShapeSections.forEach(section => {
      // Apply tablet landscape-specific styles
      section.style.width = "100%";
      section.style.maxWidth = "100%";
      section.style.paddingLeft = "0 !important";
      section.style.paddingRight = "0 !important";
      section.style.overflowX = "hidden";
      
      // Fix container margins
      const container = section.querySelector('.mattress-shape-container');
      if (container) {
        // Set margins based on screen size
        if (window.innerWidth <= 1024) {
          container.style.marginLeft = "30px !important";
          container.style.marginRight = "30px !important";
          container.style.width = "calc(100% - 60px) !important";
          container.style.maxWidth = "calc(100% - 60px) !important";
        } else {
          container.style.marginLeft = "40px !important";
          container.style.marginRight = "40px !important";
          container.style.width = "calc(100% - 80px) !important";
          container.style.maxWidth = "calc(100% - 80px) !important";
        }
        
        container.style.boxSizing = "border-box !important";
      }
    });
  }
}

function fixMobileLayout() {
  const allShapeSections = document.querySelectorAll('.mattress-shape-section');
  
  // Check if we're on a mobile-sized device
  const isMobile = window.innerWidth < 768;
  
  if (isMobile) {
    allShapeSections.forEach(section => {
      // Apply mobile-specific styles
      section.style.paddingLeft = "0 !important";
      section.style.paddingRight = "0 !important";
      
      // Fix container margins
      const container = section.querySelector('.mattress-shape-container');
      if (container) {
        if (window.innerWidth < 480) {
          // Extra small screens
          container.style.marginLeft = "10px !important";
          container.style.marginRight = "10px !important";
          container.style.width = "calc(100% - 20px) !important";
        } else {
          // Small screens
          container.style.marginLeft = "15px !important";
          container.style.marginRight = "15px !important";
          container.style.width = "calc(100% - 30px) !important";
        }
      }
      
      // Adjust header layout
      const header = section.querySelector('.mattress-shape-header');
      if (header) {
        header.style.display = "block !important";
        
        // Position toggle button
        const toggleBtn = header.querySelector('.mattress-header-right');
        if (toggleBtn) {
          toggleBtn.style.position = "absolute";
          toggleBtn.style.top = "15px";
          toggleBtn.style.right = "15px";
        }
      }
    });
  }
}

//=============================================================================
// SECTION 12: COMPATIBILITY WITH EXISTING CONNECTOR CODE
//=============================================================================

// Add compatibility with caravanConnector if it exists
if (window.caravanConnector) {
  console.log("Detected caravan connector, ensuring compatibility");
  
  // Let the connector know we're handling things now
  window.caravanConnector.useSharedScripts = true;
  
  // Optionally provide our functions to the connector namespace for compatibility
  window.caravanConnector.scrollToMattressGrid = scrollToMattressGrid;
  window.caravanConnector.updateMattressGridDisplay = updateMattressGridDisplay;
}

//=============================================================================
// SECTION 13: EVENT LISTENERS & INITIALIZATION
//=============================================================================

// Initialize resize event listener for all sections
window.addEventListener('resize', debounce(() => {
  fixMobileLayout();
  fixTabletLandscapeMargins();
  
  // Apply individual margin fixes to each section
  const allShapeSections = document.querySelectorAll('.mattress-shape-section');
  allShapeSections.forEach(section => {
    fixMargins(section);
  });
}, 250));

// Listen for custom events from other components
document.addEventListener('mattressSelectionChanged', function(event) {
  if (event.detail && event.detail.selection) {
    updateUrlBuilder(event.detail.selection);
  }
});

// Initial call to fix layouts
fixMobileLayout();
fixTabletLandscapeMargins();

//=============================================================================
// SECTION 14: TOGGLE DEBUGGING UTILITIES
//=============================================================================

function debugToggleState() {
  console.log("DEBUG: Checking all toggle states");
  
  const allShapeSections = document.querySelectorAll(".mattress-shape-section");
  
  allShapeSections.forEach(function(section, index) {
    const sectionId = section.getAttribute("data-section-id") || `unknown-${index}`;
    const header = section.querySelector(".mattress-shape-header");
    const content = section.querySelector(".mattress-shape-content");
    const toggleBtn = section.querySelector(".mattress-shape-toggle");
    
    console.log(`Section ${sectionId}:`);
    console.log(`- Section active class: ${section.classList.contains('active')}`);
    
    if (header) {
      console.log(`- Header collapsed class: ${header.classList.contains('collapsed')}`);
    } else {
      console.log(`- Header not found`);
    }
    
    if (content) {
      console.log(`- Content display: ${window.getComputedStyle(content).display}`);
      console.log(`- Content max-height: ${window.getComputedStyle(content).maxHeight}`);
      console.log(`- Content opacity: ${window.getComputedStyle(content).opacity}`);
    } else {
      console.log(`- Content not found`);
    }
    
    if (toggleBtn) {
      console.log(`- Toggle button aria-expanded: ${toggleBtn.getAttribute('aria-expanded')}`);
      const plusIcon = toggleBtn.querySelector('.toggle-icon-plus');
      const minusIcon = toggleBtn.querySelector('.toggle-icon-minus');
      
      if (plusIcon) {
        console.log(`- Plus icon display: ${window.getComputedStyle(plusIcon).display}`);
      }
      
      if (minusIcon) {
        console.log(`- Minus icon display: ${window.getComputedStyle(minusIcon).display}`);
      }
    } else {
      console.log(`- Toggle button not found`);
    }
    
    console.log('-----------------------------------');
  });
}

// Run debug after initialization
setTimeout(debugToggleState, 1000);

// Add debug to clicks
document.addEventListener('click', function(e) {
  if (e.target.closest('.mattress-shape-toggle') || e.target.closest('.mattress-shape-header')) {
    setTimeout(debugToggleState, 400); // Run debug after toggle animation
  }
});

// Enhanced toggle debugging function
function debugToggleFunctionality() {
  console.log("==== ENHANCED DEBUG TOGGLE FUNCTIONALITY ====");
  
  // Find all toggle sections
  const sections = document.querySelectorAll('.mattress-shape-section');
  console.log(`Found ${sections.length} mattress shape sections`);
  
  sections.forEach((section, index) => {
    const sectionId = section.getAttribute('data-section-id') || `unknown-${index}`;
    const header = section.querySelector('.mattress-shape-header');
    const content = section.querySelector('.mattress-shape-content');
    const toggleBtn = section.querySelector('.mattress-shape-toggle');
    
    console.log(`\nSection ${index + 1} (ID: ${sectionId}):`);
    console.log(`- Has header: ${!!header}`);
    console.log(`- Has content: ${!!content}`);
    console.log(`- Has toggle button: ${!!toggleBtn}`);
    
    if (toggleBtn) {
      const ariaExpanded = toggleBtn.getAttribute('aria-expanded');
      console.log(`- Toggle button aria-expanded: ${ariaExpanded}`);
      
      // Check if event listener is already attached to avoid duplicates
      if (!toggleBtn.hasAttribute('data-debug-listener')) {
        // Add event listener to log toggle clicks
        toggleBtn.addEventListener('click', function() {
          console.log(`Toggle button clicked for section ${sectionId}`);
          
          // Log state immediately after click
          setTimeout(() => {
            const newAriaExpanded = toggleBtn.getAttribute('aria-expanded');
            console.log(`  → New aria-expanded: ${newAriaExpanded}`);
            console.log(`  → Content display: ${window.getComputedStyle(content).display}`);
            console.log(`  → Content maxHeight: ${window.getComputedStyle(content).maxHeight}`);
            console.log(`  → Content opacity: ${window.getComputedStyle(content).opacity}`);
          }, 10);
          
          // Log state after animation should be complete
          setTimeout(() => {
            const finalAriaExpanded = toggleBtn.getAttribute('aria-expanded');
            console.log(`  → Final aria-expanded: ${finalAriaExpanded}`);
            console.log(`  → Final content display: ${window.getComputedStyle(content).display}`);
            console.log(`  → Final content maxHeight: ${window.getComputedStyle(content).maxHeight}`);
            console.log(`  → Final content opacity: ${window.getComputedStyle(content).opacity}`);
          }, 400);
        });
        
        // Mark as having debug listener
        toggleBtn.setAttribute('data-debug-listener', 'true');
      }
    }
    
    if (content) {
      console.log(`- Content current state:`);
      console.log(`  → Display: ${window.getComputedStyle(content).display}`);
      console.log(`  → MaxHeight: ${window.getComputedStyle(content).maxHeight}`);
      console.log(`  → Opacity: ${window.getComputedStyle(content).opacity}`);
      console.log(`  → Transition: ${window.getComputedStyle(content).transition}`);
    }
    
    if (header) {
      console.log(`- Header current state:`);
      console.log(`  → Class list: ${header.className}`);
    }
  });
  
  console.log("\nDebug listeners initialized or verified");
  console.log("==== END ENHANCED DEBUG TOGGLE FUNCTIONALITY ====");
}

// Wait for the main initialization to complete before running debug
setTimeout(debugToggleFunctionality, 1500);

}); 